/**
 * The structural wall walk: the room's boundary leaves (flat wall runs, door /
 * window / opening segments, alcove walls, chamfer diagonals — from
 * `roomPath`) with their inner line, the same line offset outward by the wall
 * thickness with mitered corners, and every cutout on the leaf in along-line
 * coordinates.
 *
 * Every leaf is built as a SQUARE-ENDED SLAB: its inner line, one thickness
 * across, run out past each inner corner as far as the mitered outer corner
 * reaches along it (one thickness at a right angle, less at a chamfer,
 * nothing where the room wraps around a reflex corner) and cut off square.
 * Neighbors overlap inside the corner rather than meeting on a diagonal, so
 * wherever a wall's neighbor is absent — an opening reaching the corner, or
 * the cutaway hiding that wall — the wall ends in a butt end, as built. The
 * outer perimeter (`wallOuterRing`) is still the mitered outer line.
 *
 * THE geometry both wall renderers share: the 3D shell extrudes these leaves,
 * the 2D plan draws their band — so the two views cannot disagree about where
 * a wall, a corner, or a jamb is. A cutout edge within half an inch of the end
 * of its leaf counts as reaching it, and one that reaches an end runs out
 * through the square end with it.
 */
import {
  type EdgeSegmentType,
  type FloorPlanEdgeSegment,
  type FloorPlanRoom,
  type LineFrame,
  makeLineFrame,
  type Point,
  roomCeilingHeight,
  WALL_THICKNESS,
} from '@kaiizen/kai-floor-plan';
import { leaves, type LinePath, type PerimRef } from '../math2d';
import { roomPath } from './perimeterPath';

const MITER_LIMIT = 4;
/** An opening edge this close (ft) to the end of its wall span counts as reaching it. */
export const OPENING_SNAP = 1 / 24;

/** A door / window / opening on a leaf: its along-span, its height span, and the segment it came from. */
export interface WallCutout {
  s0: number;
  s1: number;
  base: number;
  top: number;
  kind: EdgeSegmentType;
  swing?: string;
  seg: FloorPlanEdgeSegment;
}

/** One leaf of the walk, with its inner line, mitered outer line, and square-ended slab. */
export interface WallLeaf {
  ref: PerimRef;
  /** The inner (room-side) line; `perp` points outward. */
  line: LineFrame;
  innerStart: Point;
  innerEnd: Point;
  /** The mitered outer corners (the outer perimeter's vertices). */
  outerStart: Point;
  outerEnd: Point;
  /** How far the slab runs past the inner start / end (ft): the mitered corner's reach along the leaf, never negative. */
  startExtension: number;
  endExtension: number;
  cutouts: WallCutout[];
}

export const reachesStart = (c: WallCutout): boolean => c.s0 <= 1e-9;
export const reachesEnd = (c: WallCutout, L: number): boolean => c.s1 >= L - 1e-9;

/** The along-spans of [a0, a1] not covered by the (sorted, disjoint) spans given. */
function spansBetween(a0: number, a1: number, covered: readonly [number, number][]): [number, number][] {
  const spans: [number, number][] = [];
  let cursor = a0;
  for (const [s0, s1] of covered) {
    if (s0 > cursor + 1e-4) spans.push([cursor, s0]);
    cursor = Math.max(cursor, s1);
  }
  if (cursor < a1 - 1e-4) spans.push([cursor, a1]);
  return spans;
}

/** The along-spans of [0, L] not covered by the (sorted, disjoint) cutouts `pick` selects (all, by default). */
export function solidSpans(L: number, cutouts: readonly WallCutout[], pick: (c: WallCutout) => boolean = () => true): [number, number][] {
  return spansBetween(0, L, cutouts.filter(pick).map(c => [c.s0, c.s1]));
}

/** The leaf's slab in along-coords: its inner line run out through both square ends. */
export const slabRange = (leaf: WallLeaf): [number, number] => [0 - leaf.startExtension, leaf.line.length + leaf.endExtension];

/** A cutout's along-span in the slab: one reaching an end of the leaf runs out through that square end too. */
export const slabCutoutSpan = (leaf: WallLeaf, c: WallCutout): [number, number] => [
  reachesStart(c) ? 0 - leaf.startExtension : c.s0,
  reachesEnd(c, leaf.line.length) ? leaf.line.length + leaf.endExtension : c.s1,
];

/** The along-spans of the slab not covered by the cutouts `pick` selects (all, by default). */
export function slabSolidSpans(leaf: WallLeaf, pick: (c: WallCutout) => boolean = () => true): [number, number][] {
  const [a0, a1] = slabRange(leaf);
  return spansBetween(a0, a1, leaf.cutouts.filter(pick).map(c => slabCutoutSpan(leaf, c)));
}

/**
 * The point `s` along the leaf's inner line (any s: the slab's square ends
 * included) moved `side` thicknesses straight across — 0 the inner face,
 * 1 the outer. Always perpendicular: a slab has no diagonals.
 */
export function leafPoint(leaf: WallLeaf, s: number, side: number): Point {
  const { start, dir, perp } = leaf.line;
  const across = WALL_THICKNESS * side;
  return { x: start.x + dir.x * s + perp.x * across, y: start.y + dir.y * s + perp.y * across };
}

/** A cutout's vertical extent, clamped to the room height. */
function openingExtent(seg: FloorPlanEdgeSegment, H: number): { base: number; top: number } {
  const base = Math.max(0, seg.openingBase ?? 0);
  const reach = Math.max(0, H - base);
  const ext = seg.openingHeight != null ? Math.min(seg.openingHeight, reach) : reach;
  return { base, top: base + ext };
}

/** The door/window/opening cutouts on one boundary leaf, in along-coords. */
function leafCutouts(shape: FloorPlanRoom, ref: PerimRef, L: number, H: number): WallCutout[] {
  const mk = (seg: FloorPlanEdgeSegment, s0: number): WallCutout => {
    const { base, top } = openingExtent(seg, H);
    return { s0, s1: s0 + seg.width, base, top, kind: seg.edgeSegmentType, swing: seg.swing, seg };
  };
  let segs: FloorPlanEdgeSegment[] = [];
  let whole = false;
  switch (ref.k) {
    case 'seg': {
      const seg = shape[ref.edge]?.segments[ref.index];
      segs = seg ? [seg] : [];
      whole = true;
      break;
    }
    case 'alcoveWall':
      segs = shape[ref.edge]?.segments[ref.index]?.[ref.wall]?.segments ?? [];
      break;
    case 'alcoveCorner':
      segs = shape[ref.edge]?.segments[ref.index]?.[ref.which]?.segments ?? [];
      break;
    case 'roomCorner':
      segs = shape[ref.corner]?.segments ?? [];
      break;
    default:
      return [];
  }
  return segs
    .filter(s => s.edgeSegmentType !== 'alcove')
    .map(s => (whole ? { ...mk(s, 0), s1: L } : mk(s, s.position)))
    // Clamp to the leaf; an edge within OPENING_SNAP of an end lands exactly on it.
    .map(c => ({ ...c, s0: c.s0 < OPENING_SNAP ? 0 : c.s0, s1: c.s1 > L - OPENING_SNAP ? L : c.s1 }))
    .filter(c => c.s1 - c.s0 > 1e-4)
    .sort((a, b) => a.s0 - b.s0);
}

/**
 * Fold flat wall runs shorter than OPENING_SNAP into the door / window /
 * opening leaf beside them on the same edge, so an opening placed a hair short
 * of a corner (or of its neighbor) reaches it instead of leaving a paper-thin
 * panel. A run and a segment of one edge share the edge's line, so the merged
 * leaf stays collinear.
 */
function absorbSliverWalls(ls: LinePath[]): LinePath[] {
  const out = [...ls];
  for (let i = out.length - 1; i >= 0; i--) {
    const sliver = out[i];
    if (sliver.ref.k !== 'wall' || sliver.line.length >= OPENING_SNAP) continue;
    const n = out.length;
    const prevIndex = (i - 1 + n) % n, nextIndex = (i + 1) % n;
    const prev = out[prevIndex], next = out[nextIndex];
    if (prev.ref.k === 'seg' && prev.ref.edge === sliver.ref.edge) {
      out[prevIndex] = { ...prev, line: makeLineFrame(prev.line.start, sliver.line.end) };
    } else if (next.ref.k === 'seg' && next.ref.edge === sliver.ref.edge) {
      out[nextIndex] = { ...next, line: makeLineFrame(sliver.line.start, next.line.end) };
    } else continue;
    out.splice(i, 1);
  }
  return out;
}

/** Miter the outward offset of `v` between adjacent outward normals o0,o1. */
function miterOffset(v: Point, o0: Point, o1: Point, t: number): Point {
  const denom = 1 + (o0.x * o1.x + o0.y * o1.y);
  let mx: number, my: number;
  if (denom > 1e-3) {
    mx = (o0.x + o1.x) / denom;
    my = (o0.y + o1.y) / denom;
    const len = Math.hypot(mx, my);
    if (len > MITER_LIMIT) {
      mx = (mx / len) * MITER_LIMIT;
      my = (my / len) * MITER_LIMIT;
    }
  } else {
    mx = o1.x;
    my = o1.y; // near-degenerate: fall back to a square cap
  }
  return { x: v.x + t * mx, y: v.y + t * my };
}

/** The walk: every leaf in boundary order (a closed loop), or empty for a room with no walkable boundary. */
export function wallWalk(shape: FloorPlanRoom, H: number = roomCeilingHeight(shape)): WallLeaf[] {
  const ls = absorbSliverWalls(leaves(roomPath(shape)).filter(l => l.line.length > 1e-4));
  const n = ls.length;
  if (n < 3) return [];
  const O = ls.map(l => l.line.perp);
  const outerV = ls.map((l, i) => miterOffset(l.line.start, O[(i - 1 + n) % n], O[i], WALL_THICKNESS));
  // How far a mitered corner reaches along the leaf past its inner corner, in
  // the direction `sign` (−1 back past the start, +1 on past the end).
  const reach = (outer: Point, inner: Point, dir: Point, sign: number): number =>
    Math.max(0, sign * ((outer.x - inner.x) * dir.x + (outer.y - inner.y) * dir.y));
  return ls.map((l, i) => ({
    ref: l.ref,
    line: l.line,
    innerStart: l.line.start,
    innerEnd: l.line.end,
    outerStart: outerV[i],
    outerEnd: outerV[(i + 1) % n],
    startExtension: reach(outerV[i], l.line.start, l.line.dir, -1),
    endExtension: reach(outerV[(i + 1) % n], l.line.end, l.line.dir, 1),
    cutouts: leafCutouts(shape, l.ref, l.line.length, H),
  }));
}

/**
 * The room's OUTER perimeter: the structural boundary (alcoves and corner
 * chamfers included) offset outward by the wall thickness with mitered
 * corners — the footprint the floor and ceiling slabs extend to and the 2D
 * band draws out to. Empty when the room has no walkable boundary.
 */
export function wallOuterRing(shape: FloorPlanRoom): Point[] {
  return wallWalk(shape).map(leaf => leaf.outerStart);
}
